import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from "primereact/inputtext";
import { Password } from 'primereact/password';
import { classNames } from 'primereact/utils';
import React, { useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { MobXProviderContext } from "mobx-react";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import ReCaptcha from "react-google-recaptcha";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import AuthLayout from "../components/AuthLayout";
import { appFormSectionStyle } from '../../../components/general/uiSurfaceStyles';
import { PasswordPolicyFooter, passwordHeader } from '../constants/passwordPolicy';
import { useAuthExperience } from '../hooks/useAuthExperience';
import { getSessionBrandOverride } from "../config/brandOverride";

const PENDING_REGISTRATION_STORAGE_KEY = "auth.pendingRegistration";
const PUBLIC_BAA_VERSION = "2026-03-22";
const SIGNUP_ASSENT_TEXT = "I am at least 18 years of age and agree to all the following agreements and policies:";

const Register = () => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = React.useRef<Toast>(null);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const authExperience = useAuthExperience();
  const invitedEmail = String(searchParams.get("email") || "").trim().toLowerCase();
  const invitedRole = String(searchParams.get("role") || "").trim().toLowerCase();
  const roleOptions = React.useMemo(() => authExperience.roleOptions || [], [authExperience.roleOptions]);

  const defaultValues = {
    email: invitedEmail,
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    registerAsRoleType: invitedRole,
    registerAsRoleTypes: invitedRole ? [invitedRole] : [],
    userOver18: false,
  }
  const { control, formState: { errors }, handleSubmit, watch, setValue } = useForm({ defaultValues });
  const passwordValue = watch("password", "");
  const selectedRoleType = String(watch("registerAsRoleType", invitedRole) || "").trim().toLowerCase();
  const watchedRoleTypes = watch("registerAsRoleTypes", invitedRole ? [invitedRole] : []);
  const selectedRoleTypes = Array.isArray(watchedRoleTypes)
    ? watchedRoleTypes.map((role: string) => String(role || "").trim().toLowerCase()).filter(Boolean)
    : [];
  const selectedRoleOption = roleOptions.find((role) => role.value === selectedRoleType);

  React.useEffect(() => {
    const nextRoleType = invitedRole || "";
    if (nextRoleType && !selectedRoleType) {
      setValue("registerAsRoleType", nextRoleType, { shouldValidate: true });
    }
    if (nextRoleType && selectedRoleTypes.length === 0) {
      setValue("registerAsRoleTypes", [nextRoleType], { shouldValidate: true });
    }
  }, [invitedRole, selectedRoleType, selectedRoleTypes.length, setValue]);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onSubmit = async (data) => {

    if (!data.userOver18) {
      setErrorMessage("You must be at least 18 years of age and agree to the Terms of Service, Privacy Policy, and Business Associate Agreement.");
      return;
    }

    setErrorMessage("");
    setLoading(true);
    try {
      await backendAPIStore.signUpPublicUser({
        host: window.location.host,
        brandOverride: getSessionBrandOverride(),
        email: data.email.toLowerCase(),
        firstName: data.firstName,
        lastName: data.lastName,
        username: data.username.toLowerCase(),
        password: data.password,
        phoneNumber: data.phoneNumber,
        roleType: String(data.registerAsRoleType || "").toLowerCase(),
        roleTypes: [String(data.registerAsRoleType || "").trim().toLowerCase()].filter(Boolean),
        agreementAcceptance: {
          termsOfService: true,
          privacyPolicy: true,
          businessAssociateAgreement: true,
          acceptedVia: "signup_checkbox",
          routePath: window.location.pathname,
          assentTextShown: SIGNUP_ASSENT_TEXT,
          agreementVersions: {
            termsOfService: "current",
            privacyPolicy: "current",
            businessAssociateAgreement: PUBLIC_BAA_VERSION,
          },
          acceptedDocumentPaths: {
            termsOfService: RouterAddress.TERMS_OF_SERVICE_PAGE,
            privacyPolicy: RouterAddress.PRIVACY_PAGE,
            businessAssociateAgreement: RouterAddress.BUSINESS_ASSOCIATE_AGREEMENT_PAGE,
          },
        },
      });
      localStorage.setItem(PENDING_REGISTRATION_STORAGE_KEY, JSON.stringify({
        email: data.email.toLowerCase(),
        username: data.username.toLowerCase(),
        password: data.password,
        createdAt: new Date().toISOString(),
      }));
      navigate(RouterAddress.CONFIRM_REGISTRATION, {
        state: {
          email: data.email.toLowerCase(),
          username: data.username.toLowerCase(),
          password: data.password,
        }
      });
    } catch (error: any) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Sign Up" subtitle={authExperience.auth.signUpSubtitle}>
      <Toast ref={toast} />
      <form onSubmit={handleSubmit(onSubmit)} className="justify-content-center align-items-center flex-column flex" style={appFormSectionStyle}>
        <div className="field">
          <span className="p-float-label">
            <Controller name="email" control={control} rules={{
              required: 'Email is required.',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, message: 'Invalid email address. E.g. example@email.com'
              }
            }} render={({ field, fieldState }) => (
              <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="email" className={classNames({ 'p-error': errors.email })}>Email</label>
          </span>
          {getFormErrorMessage('email')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="username" control={control} rules={{ required: 'Username is required.' }} render={({ field, fieldState }) => (
              <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="username" className={classNames({ 'p-error': errors.username })}>Username</label>
          </span>
          {getFormErrorMessage('username')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="password" control={control} rules={{ required: 'Password is required.' }} render={({ field, fieldState }) => (
              <Password id={field.name} {...field} feedback={true} toggleMask
                className={classNames({ 'p-invalid': fieldState.invalid })} header={passwordHeader} footer={<PasswordPolicyFooter password={passwordValue} />} />
            )} />
            <label htmlFor="password" className={classNames({ 'p-error': errors.password })}>Password</label>
          </span>
          {getFormErrorMessage('password')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="confirmPassword" control={control} rules={{
              required: 'Confirm Password is required.',
              validate: value => value === control._formValues.password || 'Passwords do not match.'
            }}
              render={({ field, fieldState }) => (
                <Password id={field.name} {...field} feedback={false} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            <label htmlFor="confirmPassword" className={classNames({ 'p-error': errors.confirmPassword })}>Confirm Password</label>
          </span>
          {getFormErrorMessage('confirmPassword')}
        </div>

        <Divider align="center" className="w-full">
          <h5>Contact Details</h5>
        </Divider>

        <div className="field mt-4">
          <span className="p-float-label">
            <Controller name="firstName" control={control} rules={{
              required: 'First Name is required.',
              maxLength: { value: 100, message: 'First Name must be at most 100 characters long.' }
            }} render={({ field, fieldState }) => (
              <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="firstName" className={classNames({ 'p-error': errors.firstName })}>First Name</label>
          </span>
          {getFormErrorMessage('firstName')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="lastName" control={control} rules={{
              required: 'Last Name is required.',
              maxLength: { value: 100, message: 'Last Name must be at most 100 characters long.' }
            }} render={({ field, fieldState }) => (
              <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="lastName" className={classNames({ 'p-error': errors.lastName })}>Last Name</label>
          </span>
          {getFormErrorMessage('lastName')}
        </div>

        <div className="field">
          <span className="p-float-label">
            <Controller name="phoneNumber" control={control} rules={{
              required: 'Phone Number is required.',
              pattern: { value: /^[0-9()+-\s]*$/, message: 'Phone Number is not valid.' },
              maxLength: { value: 20, message: 'Phone Number must be at most 20 characters long.' }
            }} render={({ field, fieldState }) => (
              <InputText type="tel" id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            <label htmlFor="phoneNumber" className={classNames({ 'p-error': errors.phoneNumber })}>Phone Number</label>
          </span>
          {getFormErrorMessage('phoneNumber')}
        </div>

        <div className="field">
          <label htmlFor="registerAsRoleType">Primary role: &nbsp;</label>
          <Controller
            name="registerAsRoleType"
            control={control}
            rules={{ required: 'Please select your role' }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                value={field.value}
                options={roleOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => {
                  const nextRoleType = String(e.value || "").trim().toLowerCase();
                  field.onChange(nextRoleType);
                  setValue("registerAsRoleTypes", nextRoleType ? [nextRoleType] : [], { shouldValidate: false });
                }}
                placeholder="Select role"
                className={classNames({ 'p-invalid': fieldState.invalid })}
              />
            )}
          />
          {getFormErrorMessage('registerAsRoleType')}
          {selectedRoleOption?.description ? (
            <div
              className="mt-3 p-3 border-round"
              role="status"
              style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1e3a5f",
                maxWidth: "32rem",
                lineHeight: 1.5,
              }}
            >
              <div className="font-semibold mb-1">As a {selectedRoleOption.label}, you can:</div>
              <div>{selectedRoleOption.description}</div>
            </div>
          ) : null}
        </div>

        <Divider />
        <div className="field">
          <span className="">

            <Controller name="userOver18" control={control} render={({ field, fieldState }) => (
              <Checkbox id={field.name} checked={field.value} onChange={(e) => field.onChange(e.checked)}
                className={classNames({ 'p-invalid': fieldState.invalid })} />
            )} />
            &nbsp;&nbsp;

            <label htmlFor="userOver18" className={classNames({ 'p-error': errors.userOver18 })}>
              {SIGNUP_ASSENT_TEXT}
              <ul>
                <li><a href={RouterAddress.TERMS_OF_SERVICE_PAGE} target="_blank" rel="noopener noreferrer">&nbsp;Terms of Service&nbsp;</a></li>
                <li><a href={RouterAddress.PRIVACY_PAGE} target="_blank" rel="noopener noreferrer">&nbsp;Privacy Policy</a></li>
                <li><a href={RouterAddress.BUSINESS_ASSOCIATE_AGREEMENT_PAGE} target="_blank" rel="noopener noreferrer">&nbsp;Business Associate Agreement</a></li>
              </ul>
            </label>
          </span>
        </div>

        <div className="p-error mb-4">{errorMessage}</div>

        <ReCaptcha sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY} onChange={(value) => setRecaptchaValue(value)} />

        <Button type="submit" label="Register" severity="warning" rounded className="mt-2 p-button-outlined" loading={loading}
          disabled={!recaptchaValue} />

        <div className="grid" style={{ minWidth: "70%", alignItems: "center", textAlign: "center", marginTop: "1em" }}>
          <p className="col">
            Back to - &nbsp;
            <a onClick={() => navigate(RouterAddress.LOGIN_PAGE)}>
              Login
            </a>
          </p>
        </div>
      </form >
    </AuthLayout >
  );
};
export default Register;
